/**
 * EXPERIMENT — Document Picture-in-Picture window helper
 *
 * Opens a PiP window, copies font-face styles, and returns a mount point
 * so React can portal components into it.
 *
 * Kill switch: set EXPERIMENT_MINI_BUTTON = false in App.tsx / index.tsx
 */

export function isPipSupported(): boolean {
  return "documentPictureInPicture" in window;
}

let activePipWindow: Window | null = null;

export function isPipOpen(): boolean {
  return activePipWindow !== null && !activePipWindow.closed;
}

export interface PipHandle {
  pipWindow: Window;
  container: HTMLDivElement;
}

export async function openPipWindow(): Promise<PipHandle | null> {
  if (!isPipSupported()) return null;
  if (isPipOpen()) return null;

  const dPiP = (window as any).documentPictureInPicture;

  const pipWin: Window = await dPiP.requestWindow({
    width: 260,
    height: 380,
  });

  activePipWindow = pipWin;
  pipWin.addEventListener("pagehide", () => { activePipWindow = null; });

  const doc = pipWin.document;
  doc.body.style.cssText = "margin:0; overflow:hidden; background:#000;";

  /* Copy ALL stylesheets so Tailwind classes + Gilroy fonts work */
  for (const sheet of Array.from(document.styleSheets)) {
    try {
      if (sheet.href) {
        const link = doc.createElement("link");
        link.rel = "stylesheet";
        link.href = sheet.href;
        doc.head.appendChild(link);
      } else {
        const style = doc.createElement("style");
        style.textContent = Array.from(sheet.cssRules).map((r) => r.cssText).join("\n");
        doc.head.appendChild(style);
      }
    } catch { /* cross-origin sheet, skip */ }
  }

  const container = doc.createElement("div");
  container.id = "pip-root";
  doc.body.appendChild(container);

  return { pipWindow: pipWin, container };
}
